// ============================================================
// JIRA REST API CLIENT
// Based on Jira Cloud Platform REST API v3 (Postman Collection)
// ============================================================

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: Record<string, unknown>
  params?: Record<string, string | number | boolean>
}

interface SearchParams {
  jql: string
  fields?: string[]
  expand?: string[]
  maxResults?: number
  startAt?: number
  validateQuery?: 'strict' | 'warn' | 'none'
}

interface PaginatedResponse<T> {
  startAt: number
  maxResults: number
  total: number
  isLast?: boolean
  values?: T[]
  issues?: T[]
}

const getApiBaseUrl = (): string => {
  // Proxy through backend to avoid CORS
  return '/api/jira'
}

// Simple in-memory cache for frequently accessed data
const cache = new Map<string, { data: unknown; timestamp: number }>()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

function getCached<T>(key: string): T | null {
  const cached = cache.get(key)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data as T
  }
  cache.delete(key)
  return null
}

function setCache(key: string, data: unknown): void {
  cache.set(key, { data, timestamp: Date.now() })
}

export function clearJiraCache(): void {
  cache.clear()
}

class JiraClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = getApiBaseUrl()
  }

  // ============================================================
  // CORE FETCH METHOD
  // ============================================================

  async fetch<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const { method = 'GET', body, params } = options
    
    let url = `${this.baseUrl}${endpoint}`
    
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }

    const fetchOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    }

    if (body && method !== 'GET') {
      fetchOptions.body = JSON.stringify(body)
    }

    const response = await fetch(url, fetchOptions)

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Jira API Error: ${response.status} - ${errorText}`)
    }

    return response.json()
  }

  // ============================================================
  // ISSUE SEARCH (JQL)
  // POST /rest/api/3/search
  // ============================================================

  async search<T>(params: SearchParams): Promise<T> {
    const body: Record<string, unknown> = {
      jql: params.jql,
      maxResults: params.maxResults || 100,
      startAt: params.startAt || 0,
    }

    if (params.fields) {
      body.fields = params.fields
    }

    if (params.expand) {
      body.expand = params.expand
    }

    if (params.validateQuery) {
      body.validateQuery = params.validateQuery
    }

    return this.fetch<T>('/search', {
      method: 'POST',
      body,
    })
  }

  // Paginated search for large result sets
  async searchAll<T>(params: SearchParams, maxTotal: number = 1000): Promise<T[]> {
    const allResults: T[] = []
    let startAt = 0
    const maxResults = 100

    while (startAt < maxTotal) {
      const response = await this.search<PaginatedResponse<T>>({
        ...params,
        startAt,
        maxResults,
      })

      const issues = response.issues || []
      allResults.push(...issues)

      if (issues.length < maxResults || allResults.length >= (response.total || 0)) {
        break
      }

      startAt += maxResults
    }

    return allResults
  }

  // ============================================================
  // GET ISSUE
  // GET /rest/api/3/issue/:issueIdOrKey
  // ============================================================

  async getIssue<T>(
    issueKey: string, 
    fields?: string,
    expand?: string[]
  ): Promise<T> {
    const params: Record<string, string> = {}
    
    if (fields) {
      params.fields = fields
    }
    
    if (expand?.length) {
      params.expand = expand.join(',')
    }

    return this.fetch<T>(`/issue/${issueKey}`, { params })
  }

  // Get issue with changelog (for tracking history)
  async getIssueWithHistory<T>(issueKey: string, fields?: string): Promise<T> {
    return this.getIssue<T>(issueKey, fields, ['changelog'])
  }

  // ============================================================
  // WORKLOGS
  // GET /rest/api/3/issue/:issueIdOrKey/worklog
  // ============================================================

  async getWorklogs<T>(issueKey: string, startAt: number = 0): Promise<T> {
    return this.fetch<T>(`/issue/${issueKey}/worklog`, {
      params: { startAt, maxResults: 100 },
    })
  }

  // Get all worklogs for an issue
  async getAllWorklogs<T>(issueKey: string): Promise<T[]> {
    const allWorklogs: T[] = []
    let startAt = 0
    const maxResults = 100

    while (true) {
      const response = await this.fetch<{ worklogs: T[]; total: number }>(
        `/issue/${issueKey}/worklog`,
        { params: { startAt, maxResults } }
      )

      allWorklogs.push(...response.worklogs)

      if (allWorklogs.length >= response.total) {
        break
      }

      startAt += maxResults
    }

    return allWorklogs
  }

  // ============================================================
  // USERS
  // GET /rest/api/3/user
  // GET /rest/api/3/users/search
  // ============================================================

  async getUser<T>(accountId: string): Promise<T> {
    const cacheKey = `user:${accountId}`
    const cached = getCached<T>(cacheKey)
    if (cached) return cached

    const result = await this.fetch<T>('/user', {
      params: { accountId },
    })

    setCache(cacheKey, result)
    return result
  }

  async getUsers<T>(): Promise<T> {
    const cacheKey = 'users:all'
    const cached = getCached<T>(cacheKey)
    if (cached) return cached

    const result = await this.fetch<T>('/users')
    setCache(cacheKey, result)
    return result
  }

  async searchUsers<T>(query: string, maxResults: number = 50): Promise<T[]> {
    return this.fetch<T[]>('/user/search', {
      params: { query, maxResults },
    })
  }

  // ============================================================
  // PROJECTS
  // GET /rest/api/3/project
  // ============================================================

  async getProjects<T>(expand?: string[]): Promise<T[]> {
    const cacheKey = 'projects:all'
    const cached = getCached<T[]>(cacheKey)
    if (cached) return cached

    const params: Record<string, string> = {}
    if (expand?.length) {
      params.expand = expand.join(',')
    }

    const result = await this.fetch<T[]>('/project', { params })
    setCache(cacheKey, result)
    return result
  }

  async getProject<T>(projectKey: string, expand?: string[]): Promise<T> {
    const cacheKey = `project:${projectKey}`
    const cached = getCached<T>(cacheKey)
    if (cached) return cached

    const params: Record<string, string> = {}
    if (expand?.length) {
      params.expand = expand.join(',')
    }

    const result = await this.fetch<T>(`/project/${projectKey}`, { params })
    setCache(cacheKey, result)
    return result
  }

  // ============================================================
  // BULK OPERATIONS (Optimized)
  // ============================================================

  // Get multiple issues in parallel with rate limiting
  async getIssuesBatch<T>(
    issueKeys: string[], 
    fields?: string,
    batchSize: number = 10
  ): Promise<Map<string, T>> {
    const results = new Map<string, T>()
    
    for (let i = 0; i < issueKeys.length; i += batchSize) {
      const batch = issueKeys.slice(i, i + batchSize)
      const promises = batch.map(key => 
        this.getIssue<T>(key, fields)
          .then(issue => ({ key, issue }))
          .catch(() => ({ key, issue: null }))
      )
      
      const batchResults = await Promise.all(promises)
      batchResults.forEach(({ key, issue }) => {
        if (issue) {
          results.set(key, issue)
        }
      })
    }

    return results
  }

  // Search with JQL and get detailed info in one call
  async searchWithDetails<T>(
    jql: string,
    additionalFields: string[] = []
  ): Promise<T[]> {
    const baseFields = [
      'summary',
      'description',
      'issuetype',
      'status',
      'project',
      'priority',
      'assignee',
      'reporter',
      'created',
      'updated',
      'resolutiondate',
      'duedate',
      'issuelinks',
      'parent',
      'labels',
      'components',
      'timetracking',
      'worklog',
      'timespent',
      'timeoriginalestimate',
      'progress',
    ]

    const allFields = [...new Set([...baseFields, ...additionalFields])]

    return this.searchAll<T>({
      jql,
      fields: allFields,
      expand: ['changelog'],
    })
  }

  // ============================================================
  // UTILITY METHODS
  // ============================================================

  // Check if Jira is configured and accessible
  async healthCheck(): Promise<boolean> {
    try {
      await this.fetch('/myself')
      return true
    } catch {
      return false
    }
  }

  // Get current user info
  async getCurrentUser<T>(): Promise<T> {
    return this.fetch<T>('/myself')
  }

  // Get server info
  async getServerInfo<T>(): Promise<T> {
    return this.fetch<T>('/serverInfo')
  }
}

export const jiraClient = new JiraClient()
